// Sort Select
// ------------
// Standardised dropdown affordance used across the dashboard, the
// WebApplication vuln table, the Applications page, and the test
// selector on the WebApplication page. Originally named for the
// sort-context use, but is the canonical dropdown for any short
// single-select menu in the app.
//
// Implemented as a custom popover rather than a native <select> so
// we have explicit control over the open-state max-height + scroll —
// without that, a webapp with 50+ historical tests would open a
// browser-controlled list that varies in length per platform. The
// trade-off is we own keyboard / screen-reader / outside-click /
// focus-return behavior explicitly rather than getting it from the
// browser. Both are implemented below.
//
// Keyboard behavior:
//   - Trigger focused, closed → Enter / Space / ArrowDown opens, with
//     the currently-selected item active
//   - Listbox open → ArrowUp / ArrowDown move the active item with
//     wraparound; Home / End jump to first / last
//   - Enter / Space picks the active item and closes
//   - Escape closes without changing selection, returns focus to
//     trigger
//   - Tab closes and lets the browser move focus naturally
//   - Click outside closes
//
// Accessibility:
//   - Trigger: aria-haspopup="listbox", aria-expanded reflects state
//   - Listbox: role="listbox", aria-activedescendant points at the
//     active option (no focus shift between items — keeps DOM focus
//     on the trigger so screen readers stay anchored)
//   - Options: role="option", aria-selected on the picked one

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ArrowUpDown, Check, ChevronDown, type LucideIcon } from "lucide-react";

export interface SortOption<TValue extends string> {
  value: TValue;
  label: string;
}

interface SortSelectProps<TValue extends string> {
  value: TValue;
  onChange: (value: TValue) => void;
  options: ReadonlyArray<SortOption<TValue>>;
  ariaLabel: string;
  id?: string;
  // Optional leading icon. Defaults to ArrowUpDown (sort context).
  icon?: LucideIcon;
  // Fill the container and truncate, instead of sizing to the widest
  // option label.
  //
  // The default sizing (see the trigger below) stacks every label to keep the
  // trigger width stable as the selection changes — correct for short sort
  // labels. It is wrong when the labels are long and variable, e.g. a user
  // picker rendering "Name (email@example.com)": the trigger grows to the
  // longest one and pushes its container wide, which is how the Transfer apps
  // dialog ended up with a horizontal scrollbar. In a form field the width is
  // already fixed by the field, so there is nothing to stabilise.
  fullWidth?: boolean;
  // Form-field error state. Mirrors the `hasError` prop on Input /
  // Textarea / the legacy native Select wrapper — when true, the
  // trigger uses the red border + ring so the field reads as invalid
  // alongside a red error message rendered by the surrounding
  // FormField. Form contexts only; ignored for inline-sort uses.
  hasError?: boolean;
}

// Open-state max height. ~9 rows; longer lists scroll inside.
const POPOVER_MAX_HEIGHT_PX = 288;
// Gap between the trigger and the popover edge.
const POPOVER_GAP_PX = 4;
// Breathing room so a wide popover never sits flush against the viewport.
const POPOVER_VIEWPORT_MARGIN_PX = 16;

export default function SortSelect<TValue extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  id,
  icon: Icon = ArrowUpDown,
  fullWidth = false,
  hasError = false,
}: SortSelectProps<TValue>) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  // Popover position is computed from the trigger's bounding rect on
  // open and on resize/scroll. Rendered via portal in document.body
  // with position:fixed so no parent's overflow / transform / flex
  // alignment can clip, push, or shift the popover. openUpward auto-
  // flips when there isn't enough room below the trigger.
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);

  // Stable per-instance ids for aria wiring. listboxId is the
  // popover; optionIdFor(i) lets aria-activedescendant point at the
  // active row.
  const reactId = useId();
  const listboxId = `sortselect-${reactId}-listbox`;
  const optionIdFor = (index: number) =>
    `sortselect-${reactId}-option-${index}`;

  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.value === value),
    [options, value],
  );

  // Compute the popover's fixed-position style from the current
  // trigger rect. Auto-flips above the trigger when there isn't
  // enough room below, so the popover always stays in the viewport.
  const computePopoverStyle = useCallback((): React.CSSProperties => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return {};
    const rect = triggerEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward =
      POPOVER_MAX_HEIGHT_PX + POPOVER_GAP_PX > spaceBelow &&
      spaceAbove > spaceBelow;
    const baseStyle: React.CSSProperties = {
      position: "fixed",
      minWidth: rect.width,
      // Right-align with the trigger to match the previous in-flow
      // behavior (the dropdown extended rightward from its trigger).
      right: Math.max(0, window.innerWidth - rect.right),
      // Without a cap the list grows to its widest option (they are
      // whitespace-nowrap), so a long label could run off-screen and the
      // per-option `truncate` would never engage. Full-width triggers cap to
      // the trigger itself; others may exceed it but never the viewport.
      maxWidth: fullWidth
        ? rect.width
        : Math.max(0, window.innerWidth - POPOVER_VIEWPORT_MARGIN_PX),
      maxHeight: POPOVER_MAX_HEIGHT_PX,
    };
    if (openUpward) {
      baseStyle.bottom = window.innerHeight - rect.top + POPOVER_GAP_PX;
    } else {
      baseStyle.top = rect.bottom + POPOVER_GAP_PX;
    }
    return baseStyle;
  }, [fullWidth]);

  // Open / close helpers. Opening seeds activeIndex from the current
  // selection so keyboard nav starts from where the user is, rather
  // than at the top. Popover style is computed at open time and on
  // every reposition trigger (resize / scroll) below.
  const open = useCallback(() => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setPopoverStyle(computePopoverStyle());
    setIsOpen(true);
  }, [selectedIndex, computePopoverStyle]);

  const close = useCallback((opts?: { restoreFocus?: boolean }) => {
    setIsOpen(false);
    if (opts?.restoreFocus) {
      // Return focus to the trigger so screen readers stay anchored
      // and Tab continues to behave naturally.
      triggerRef.current?.focus();
    }
  }, []);

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option) return;
      onChange(option.value);
      close({ restoreFocus: true });
    },
    [options, onChange, close],
  );

  // Outside click → close. Listening on mousedown so we close before
  // the click handler on a downstream button fires (e.g. clicking a
  // tile elsewhere on the page should close the open dropdown without
  // also fighting the tile's onClick).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (clickEvent: MouseEvent) => {
      const target = clickEvent.target as Node | null;
      if (
        triggerRef.current?.contains(target ?? null) ||
        listboxRef.current?.contains(target ?? null)
      ) {
        return;
      }
      close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, close]);

  // Scroll the active option into view as the user arrows through —
  // otherwise long lists would silently move the active item off-
  // screen.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const listbox = listboxRef.current;
    if (!listbox) return;
    const optionEl = listbox.children[activeIndex] as HTMLElement | undefined;
    optionEl?.scrollIntoView({ block: "nearest" });
  }, [isOpen, activeIndex]);

  // Reposition the popover on window resize and page scroll so it
  // tracks the trigger if either moves while the popover is open. We
  // listen with capture:true on scroll because nested scrollable
  // ancestors (e.g. the page main area inside a parent overflow)
  // don't bubble scroll to window.
  useEffect(() => {
    if (!isOpen) return undefined;
    const reposition = () => setPopoverStyle(computePopoverStyle());
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [isOpen, computePopoverStyle]);

  const handleTriggerKeyDown = (
    keyEvent: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (
      keyEvent.key === "ArrowDown" ||
      keyEvent.key === "ArrowUp" ||
      keyEvent.key === "Enter" ||
      keyEvent.key === " "
    ) {
      keyEvent.preventDefault();
      open();
    }
  };

  const handleListboxKeyDown = (
    keyEvent: React.KeyboardEvent<HTMLUListElement>,
  ) => {
    if (options.length === 0) return;

    switch (keyEvent.key) {
      case "ArrowDown":
        keyEvent.preventDefault();
        setActiveIndex((current) =>
          current >= options.length - 1 ? 0 : current + 1,
        );
        break;
      case "ArrowUp":
        keyEvent.preventDefault();
        setActiveIndex((current) =>
          current <= 0 ? options.length - 1 : current - 1,
        );
        break;
      case "Home":
        keyEvent.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        keyEvent.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        keyEvent.preventDefault();
        if (activeIndex >= 0) commit(activeIndex);
        break;
      case "Escape":
        keyEvent.preventDefault();
        close({ restoreFocus: true });
        break;
      case "Tab":
        // Don't preventDefault — let the browser move focus naturally;
        // we just close so the popover doesn't linger.
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`relative items-center gap-2 ${
        fullWidth ? "flex w-full min-w-0" : "inline-flex"
      }`}
    >
      <Icon className="h-4 w-4 text-gray-400 shrink-0" />
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && activeIndex >= 0 ? optionIdFor(activeIndex) : undefined
        }
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleTriggerKeyDown}
        className={`inline-flex items-center justify-between gap-2 h-9 text-sm font-medium text-gray-700 bg-white border rounded-md pl-3 pr-2 focus:outline-none focus:ring-2 cursor-pointer ${
          fullWidth ? "w-full min-w-0" : ""
        } ${
          hasError
            ? "border-red-300 hover:border-red-400 focus:ring-red-300"
            : "border-gray-200 hover:border-gray-300 focus:ring-blue-300"
        }`}
      >
        {/* Full width: the field already fixes the width, so render only the
            selected label and let it truncate. Stacking every label here is
            what made the trigger grow to its longest option. */}
        {fullWidth ? (
          <span
            className={`min-w-0 truncate text-left ${
              selectedIndex < 0 ? "text-gray-400" : ""
            }`}
          >
            {selectedIndex >= 0 ? options[selectedIndex].label : ariaLabel}
          </span>
        ) : (
        /* Stable trigger width — all option labels are stacked in
            the same grid cell; only the selected one is visible. The
            container sizes to the widest label, so switching between
            short labels ("Live") and long ones ("Invalid",
            "Completed") doesn't change the trigger width. */
        <span className="grid">
          {options.map((option) => (
            <span
              key={option.value}
              aria-hidden={option.value !== value}
              className="row-start-1 col-start-1 whitespace-nowrap"
              style={{
                visibility: option.value === value ? "visible" : "hidden",
              }}
            >
              {option.label}
            </span>
          ))}
          {/* Fallback rendered when nothing in `options` matches
              `value` (selectedIndex < 0). Hidden when there IS a
              match — the option above takes over and is visible.
              Keeps the trigger from collapsing when value is empty,
              and surfaces the ariaLabel for screen readers. */}
          {selectedIndex < 0 && (
            <span className="row-start-1 col-start-1 whitespace-nowrap text-gray-400">
              {ariaLabel}
            </span>
          )}
        </span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {isOpen &&
        createPortal(
          <ul
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            onKeyDown={handleListboxKeyDown}
            style={popoverStyle}
            className="z-50 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg py-1 focus:outline-none"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === activeIndex;
              return (
                <li
                  key={option.value}
                  id={optionIdFor(index)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => commit(index)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={[
                    "flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer whitespace-nowrap",
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50",
                  ].join(" ")}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && (
                    <Check
                      className={`h-3.5 w-3.5 shrink-0 ${
                        isActive ? "text-blue-600" : "text-gray-400"
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
